import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-center items-center py-8 gap-5 w-full max-w-md" }
const _hoisted_2 = { class: "tour-detect-input flex justify-center items-end w-full" }
const _hoisted_3 = { class: "form-control w-full" }
const _hoisted_4 = { class: "join" }
const _hoisted_5 = {
  class: "tour-detect-upload tooltip",
  "data-tip": "Compute firmware hash from uploaded firmware file"
}
const _hoisted_6 = {
  class: "tour-detect-load tooltip",
  "data-tip": "Load detected configuration"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "form-control w-full" }
const _hoisted_9 = { class: "overflow-x-auto" }
const _hoisted_10 = {
  key: 0,
  class: "table"
}
const _hoisted_11 = {
  key: 1,
  class: "loading"
}
const _hoisted_12 = { key: 2 }

import { computed, ref, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import { BuildJobShort } from "@/generated/api";
import { fs, assertUnreachable } from "@/utils";
import { useBackendStore, useKeyboardStore } from "@/stores";
import { BUILD_STATUS } from "@/stores/firmware";
import FileUpload from "@/components/utils/FileUpload.vue";

const HASH_NAME = "SHA-256";
const HASH_HEX_LEN = (256 / 8) * 2;
const GET_BY_HASH_DEBOUNCE_MS = 500;

type ParsedFileName = {
  board: string;
  rev: string;
  build_id: number;
};
type ParsedBuildId = { build_id: number };
type ParsedHash = { hash: string };
type ParseInputResult = ParsedFileName | ParsedBuildId | ParsedHash | null;


export default /*@__PURE__*/_defineComponent({
  __name: 'DetectArea',
  setup(__props) {

const backend = useBackendStore();
const kb = useKeyboardStore();
const toast = useToast();

onMounted(async () => {
  await backend.fetchBuildJobList();
});

const input = ref("");
const jobInfo = ref<BuildJobShort | null>(null);
const pendingDebounce = ref<number | null>(null);
const pendingFetch = ref(false);
const configLoadOngoing = ref(false);

const parsedInput = computed(() => parseInput(input.value));

const configShort = computed(() => {
  const configId = jobInfo.value?.config;
  if (configId == null) return null;
  return backend.configList.find((c) => c.id == configId);
});

const jobInfoTable = computed(() => {
  const job = jobInfo.value;
  if (!job) return null;
  return [
    ["Build ID", job.id.toString()],
    ["Status", (job.status && BUILD_STATUS[job.status]) ?? job.status],
    [
      "Date",
      new Date(
        Date.parse(job.date_finished ?? job.date_started ?? job.date_created)
      ).toLocaleString(),
    ],
    [
      "Firmware revision",
      job.firmware_revision ? job.firmware_revision.substring(0, 7) : "",
    ],
    ["Configuration", configShort.value?.name ?? "-"],
    ["Owner", configShort.value?.owner_name ?? "-"],
  ];
});

const parseInput = (text: string): ParseInputResult => {
  const num = Number(text);
  if (Number.isInteger(num)) {
    return { build_id: num };
  }

  const hash_match = text.match(/^\s*([a-fA-F0-9]+)\s*$/);
  if (hash_match?.[0].length == HASH_HEX_LEN) {
    return { hash: hash_match[1] };
  }

  const [head, suffix] = fs.splitExt(text);
  if (suffix.length > 0 && suffix != ".bin") return null;
  const basename = fs.baseName(head);

  const parts = basename.split("-");
  if (parts.length != 3) {
    return null;
  }
  const build_id = parts[2].length > 0 ? Number(parts[2]) : NaN;
  if (!Number.isInteger(build_id)) return null;

  return { board: parts[0], rev: parts[1], build_id };
};

const getByHashStop = () => {
  clearTimeout(pendingDebounce.value ?? undefined);
  pendingDebounce.value = null;
  pendingFetch.value = false;
};

const getByHash = (hash: string) => {
  const job = backend.buildJobsByResultsHashCache.get(hash)?.[0];
  if (job) {
    jobInfo.value = job;
    return;
  }

  getByHashStop();

  pendingDebounce.value = setTimeout(async () => {
    pendingFetch.value = true;
    const jobs = await backend.fetchBuildJobsByResultsHash(hash);

    if (pendingFetch.value) {
      pendingFetch.value = false;
      jobInfo.value = jobs[0];
    }

    getByHashStop();
  }, GET_BY_HASH_DEBOUNCE_MS);
};

const getJobInfo = async (id?: number) => {
  if (id == null) {
    jobInfo.value = null;
    return;
  }
  jobInfo.value = backend.buildJobList.find((job) => job.id == id) ?? null;
};

watch(
  parsedInput,
  async (newInput: ParseInputResult, _oldInput: ParseInputResult) => {
    getByHashStop();

    if (newInput == null) {
      jobInfo.value = null;
      return;
    }

    if ("build_id" in newInput) {
      getJobInfo(newInput.build_id);
    } else if ("hash" in newInput) {
      getByHash(newInput.hash);
    } else {
      assertUnreachable(newInput);
    }
  }
);

const loadConfig = async () => {
  const id = jobInfo.value?.id;
  if (id == undefined) return;

  try {
    configLoadOngoing.value = true;

    const job = await backend.getFullBuildJob(id);
    if (!job.config_snapshot) return;

    kb.setConfig(job.config_snapshot);
    if (backend.currentConfig != null) backend.currentConfig.name = input.value;
    toast.success("Loaded configuration from snapshot");
  } finally {
    configLoadOngoing.value = false;
  }
};

const setHashFromFile = async (_file: File, data: ArrayBuffer | string) => {
  if (!crypto?.subtle?.digest) {
    toast.error("Not supported in this browser");
    return;
  }

  const hash_buf = await crypto.subtle.digest(HASH_NAME, data as ArrayBuffer);
  const hash_u8 = new Uint8Array(hash_buf);
  const hash_hexstr = Array.from(hash_u8)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  input.value = hash_hexstr;
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", { class: "label pl-3" }, [
          _createElementVNode("span", { class: "label-text" }, "Firmware filename / Build ID / Firmware hash (" + _toDisplayString(HASH_NAME) + ")")
        ]),
        _withDirectives(_createElementVNode("input", {
          type: "text",
          placeholder: "e.g. 'ghanima-1234567-8.bin' or '8' or '8db45a225b2fb8499c63f6525da3170ed6c31252c4e0096055edecf989fafd29'",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((input).value = $event)),
          class: "input input-bordered w-full"
        }, null, 512), [
          [_vModelText, input.value]
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(FileUpload, {
          accept: ".bin",
          method: "readAsArrayBuffer",
          onLoaded: setHashFromFile,
          class: "join-item"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_Icon, {
              icon: "ic:baseline-upload",
              class: "text-lg"
            }),
            _cache[1] || (_cache[1] = _createTextVNode(" Upload file "))
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "btn btn-accent join-item",
          disabled: jobInfo.value == null || configLoadOngoing.value,
          onClick: loadConfig
        }, " Load config ", 8, _hoisted_7)
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _cache[2] || (_cache[2] = _createElementVNode("label", { class: "label pl-2" }, [
        _createElementVNode("span", { class: "label-text" }, "Detection result:")
      ], -1)),
      _createElementVNode("div", _hoisted_9, [
        (jobInfo.value != null)
          ? (_openBlock(), _createElementBlock("table", _hoisted_10, [
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(jobInfoTable.value, (row, i) => {
                  return (_openBlock(), _createElementBlock("tr", { key: i }, [
                    _createElementVNode("th", null, _toDisplayString(row[0]), 1),
                    _createElementVNode("td", null, _toDisplayString(row[1]), 1)
                  ]))
                }), 128))
              ])
            ]))
          : (pendingDebounce.value || pendingFetch.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11))
            : (_openBlock(), _createElementBlock("div", _hoisted_12, "Not found or is private to other user"))
      ])
    ])
  ]))
}
}

})